import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

const ToastCtx = createContext(null);

/** How long a toast stays on screen. */
const VISIBLE_MS = 3500;

/**
 * A repeat of the same message inside this window is treated as the same event
 * rather than a new one. Long enough to absorb a handler that fires twice,
 * short enough that a user who genuinely retries and fails again still sees
 * fresh feedback — pressing Save twice and getting one error is right;
 * pressing Save, waiting, and pressing again with no response would not be.
 */
const DEDUPE_MS = 700;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  /**
   * R5-11: ids came from Date.now(), so two toasts raised in the same
   * millisecond — which is exactly what happens when one submit fails several
   * validation rules at once — shared a React key. Duplicate keys make
   * reconciliation unreliable, and the page showed each message twice.
   *
   * A counter cannot collide.
   */
  const nextId = useRef(0);

  /**
   * Timers are tracked so they can be cleared on unmount. Previously each
   * toast left a setTimeout running that called setState on a dead component.
   */
  const timers = useRef(new Map());
  const lastShown = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((t) => t.filter((x) => x.id !== id));
    const handle = timers.current.get(id);
    if (handle) {
      clearTimeout(handle);
      timers.current.delete(id);
    }
  }, []);

  const toast = useCallback((message, type = 'default') => {
    const key = `${type}:${message}`;
    const now = Date.now();

    /**
     * The same message arriving twice in quick succession is one event the
     * user should be told about once. The existing toast's timer is restarted
     * so the message still sits on screen for its full duration.
     */
    const previous = lastShown.current.get(key);
    if (previous && now - previous.at < DEDUPE_MS) {
      lastShown.current.set(key, { at: now, id: previous.id });
      const handle = timers.current.get(previous.id);
      if (handle) clearTimeout(handle);
      timers.current.set(
        previous.id,
        setTimeout(() => dismiss(previous.id), VISIBLE_MS)
      );
      return;
    }

    const id = ++nextId.current;
    lastShown.current.set(key, { at: now, id });
    setToasts((t) => [...t, { id, message, type }]);
    timers.current.set(id, setTimeout(() => dismiss(id), VISIBLE_MS));
  }, [dismiss]);

  useEffect(() => {
    const handles = timers.current;
    return () => {
      handles.forEach((h) => clearTimeout(h));
      handles.clear();
    };
  }, []);

  return (
    <ToastCtx.Provider value={toast}>
      {children}
      {/*
        role=status with aria-live=polite so a screen reader hears the message.
        Polite rather than assertive: a save confirmation should wait its turn
        rather than interrupt whatever is being read.
      */}
      <div className="toast-wrap" role="status" aria-live="polite" aria-atomic="false">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.type}`}>{t.message}</div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
};

export const useToast = () => useContext(ToastCtx);
